import * as React from "react"
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react"
import { cn } from "@/infrastructure/lib/utils"
import { ButtonProps, buttonVariants } from "@/shared/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select"

const Pagination = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn("mx-auto flex w-full justify-between items-center", className)}
    ref={ref}
    {...props}
  />
))
Pagination.displayName = "Pagination"

const PaginationContent = React.forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLUListElement>
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn("flex flex-row items-center gap-0.5", className)}
    {...props}
  />
))
PaginationContent.displayName = "PaginationContent"

const PaginationItem = React.forwardRef<
  HTMLLIElement,
  React.HTMLAttributes<HTMLLIElement>
>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn("", className)} {...props} />
))
PaginationItem.displayName = "PaginationItem"

type PaginationLinkProps = {
  isActive?: boolean
} & Pick<ButtonProps, "size"> &
  React.ButtonHTMLAttributes<HTMLButtonElement>

const PaginationLink = ({
  className,
  isActive,
  size: _size = "icon",
  ...props
}: PaginationLinkProps) => (
  <button
    aria-current={isActive ? "page" : undefined}
    className={cn(
      buttonVariants({
        variant: "ghost",
        size: "icon",
      }),
      "h-8 w-8 rounded-md transition-colors",
      isActive && "bg-muted hover:bg-muted text-foreground font-medium",
      !isActive && "text-muted-foreground/60 hover:text-foreground hover:bg-muted/30",
      className
    )}
    {...props}
  />
)
PaginationLink.displayName = "PaginationLink"

const PaginationPrevious = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Ir para página anterior"
    className={cn(
      "text-muted-foreground/60 hover:text-foreground hover:bg-muted/30",
      "transition-colors",
      className
    )}
    {...props}
  >
    <ChevronLeft className="h-4 w-4" />
  </PaginationLink>
)
PaginationPrevious.displayName = "PaginationPrevious"

const PaginationNext = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Ir para próxima página"
    className={cn(
      "text-muted-foreground/60 hover:text-foreground hover:bg-muted/30",
      "transition-colors",
      className
    )}
    {...props}
  >
    <ChevronRight className="h-4 w-4" />
  </PaginationLink>
)
PaginationNext.displayName = "PaginationNext"

const PaginationEllipsis = ({
  className,
  ...props
}: React.ComponentProps<"span">) => (
  <span
    aria-hidden
    className={cn("flex h-8 w-8 items-center justify-center text-muted-foreground/40", className)}
    {...props}
  >
    <MoreHorizontal className="h-4 w-4" />
    <span className="sr-only">Mais páginas</span>
  </span>
)
PaginationEllipsis.displayName = "PaginationEllipsis"

interface PaginationSizeSelectProps {
  pageSize: number
  onPageSizeChange: (size: number) => void
  className?: string
}

const PaginationSizeSelect = ({
  pageSize,
  onPageSizeChange,
  className
}: PaginationSizeSelectProps) => (
  <div className={cn("flex items-center gap-2 text-sm", className)}>
    <span className="text-muted-foreground">Exibir</span>
    <Select value={pageSize.toString()} onValueChange={(value) => onPageSizeChange(Number(value))}>
      <SelectTrigger className="h-8 w-[70px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="10">10</SelectItem>
        <SelectItem value="20">20</SelectItem>
        <SelectItem value="30">30</SelectItem>
        <SelectItem value="50">50</SelectItem>
        <SelectItem value="100">100</SelectItem>
      </SelectContent>
    </Select>
    <span className="text-muted-foreground">por página</span>
  </div>
)
PaginationSizeSelect.displayName = "PaginationSizeSelect"

interface PaginationInfoProps {
  total: number
  className?: string
}

const PaginationInfo = ({
  total,
  className
}: PaginationInfoProps) => (
  <div className={cn("text-sm flex items-baseline gap-1.5", className)}>
    <span className="text-lg font-medium text-foreground">
      {total.toLocaleString()}
    </span>
    <span className="text-muted-foreground">
      registros
    </span>
  </div>
)
PaginationInfo.displayName = "PaginationInfo"

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationSizeSelect,
  PaginationInfo,
}
